// Production-ready geocoding service
// Primary: ACKO Drive internal geocoding API
// Fallbacks: Google Maps API, MapBox, BigDataCloud

import { REVERSE_GEOCODE_URL } from "./endpoints";

export interface GeocodeResult {
  city: string | null;
  country: string | null;
  state: string | null;
  confidence: "high" | "medium" | "low";
  source: string;
}

// ACKO Drive Internal Geocoding API (Primary)
// Expected response format:
// {
//   "city": "Delhi",
//   "state": "Delhi",
//   "country": "India",
//   "confidence": "high",
//   "source": "google_maps"
// }
async function geocodeWithAckoDrive(
  lat: number,
  lon: number
): Promise<GeocodeResult | null> {
  try {
    const response = await fetch(
      `${REVERSE_GEOCODE_URL}?lat=${lat}&lon=${lon}`
    );

    if (!response.ok) {
      console.warn(`ACKO Drive geocoding API returned ${response.status}`);
      return null;
    }

    const data = await response.json();

    // Validate response structure
    if (!data.city) {
      console.warn("ACKO Drive geocoding API returned no city data");
      return null;
    }

    return {
      city: data.city,
      state: data.state || null,
      country: data.country || null,
      confidence: data.confidence || "high",
      source: data.source || "acko_drive_internal",
    };
  } catch (error) {
    console.error("ACKO Drive geocoding failed:", error);
    return null;
  }
}

// Google Maps Geocoding (Fallback)
async function geocodeWithGoogleMaps(
  lat: number,
  lon: number
): Promise<GeocodeResult | null> {
  const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;

  if (!apiKey) {
    console.warn(
      "Google Maps API key not found. Add NEXT_PUBLIC_GOOGLE_MAPS_API_KEY to your .env.local"
    );
    return null;
  }

  try {
    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lon}&key=${apiKey}&language=en`
    );

    if (!response.ok) return null;

    const data = await response.json();

    if (data.status !== "OK" || !data.results?.length) return null;

    const result = data.results[0];
    const components = result.address_components;

    // Extract city with fallback hierarchy
    const city =
      components.find((c: any) => c.types.includes("locality"))?.long_name ||
      components.find((c: any) => c.types.includes("sublocality"))?.long_name ||
      components.find((c: any) =>
        c.types.includes("administrative_area_level_2")
      )?.long_name ||
      null;

    const state =
      components.find((c: any) =>
        c.types.includes("administrative_area_level_1")
      )?.long_name || null;
    const country =
      components.find((c: any) => c.types.includes("country"))?.long_name ||
      null;

    return {
      city,
      state,
      country,
      confidence: "high",
      source: "google_maps",
    };
  } catch (error) {
    console.error("Google Maps geocoding failed:", error);
    return null;
  }
}

// MapBox Geocoding (Good alternative)
async function geocodeWithMapBox(
  lat: number,
  lon: number
): Promise<GeocodeResult | null> {
  const apiKey = process.env.NEXT_PUBLIC_MAPBOX_API_KEY;

  if (!apiKey) {
    console.warn(
      "MapBox API key not found. Add NEXT_PUBLIC_MAPBOX_API_KEY to your .env.local"
    );
    return null;
  }

  try {
    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${lon},${lat}.json?access_token=${apiKey}&language=en&types=place`
    );

    if (!response.ok) return null;

    const data = await response.json();

    if (!data.features?.length) return null;

    const feature = data.features[0];
    const city = feature.text;

    // Extract state and country from context
    const context = feature.context || [];
    const state =
      context.find((c: any) => c.id.startsWith("region"))?.text || null;
    const country =
      context.find((c: any) => c.id.startsWith("country"))?.text || null;

    return {
      city,
      state,
      country,
      confidence: "high",
      source: "mapbox",
    };
  } catch (error) {
    console.error("MapBox geocoding failed:", error);
    return null;
  }
}

// Free fallback API
async function geocodeWithBigDataCloud(
  lat: number,
  lon: number
): Promise<GeocodeResult | null> {
  try {
    const response = await fetch(
      `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=en`
    );

    if (!response.ok) return null;

    const data = await response.json();
    const city = data.city || data.locality || null;

    return {
      city,
      state: data.principalSubdivision || null,
      country: data.countryName || null,
      confidence: "medium",
      source: "bigdatacloud",
    };
  } catch (error) {
    console.error("BigDataCloud geocoding failed:", error);
    return null;
  }
}

// Main geocoding function with fallbacks
export async function reverseGeocode(
  lat: number,
  lon: number
): Promise<GeocodeResult | null> {
  let result = await geocodeWithAckoDrive(lat, lon);
  if (result?.city) {
    return result;
  }

  result = await geocodeWithGoogleMaps(lat, lon);
  if (result?.city) {
    console.log(`✅ Google Maps success: ${result.city}`);
    return result;
  }

  // Try MapBox as fallback
  result = await geocodeWithMapBox(lat, lon);
  if (result?.city) {
    console.log(`✅ MapBox success: ${result.city}`);
    return result;
  }

  // Try BigDataCloud as final fallback
  result = await geocodeWithBigDataCloud(lat, lon);
  if (result?.city) {
    console.log(`✅ BigDataCloud success: ${result.city}`);
    return result;
  }

  console.error("🚫 All geocoding services failed");
  return null;
}

// Legacy function for backward compatibility
export async function reverseGeocodeCity(
  lat: number,
  lon: number
): Promise<string | null> {
  const result = await reverseGeocode(lat, lon);
  return result?.city || null;
}
